import { useState } from 'react'
import { cn } from '@/lib/utils'
import { fmtNumber, fmtRelative } from '@/shared/lib/format'
import type {
  GraphAlert, NarrativeNode, PlatformNode, SubjectNode, ThreadNode,
} from '../api'
import { SEVERITY_INK, threatInk } from './palette'

/**
 * The console's rails.
 *
 * ## Everything here is a control, not a readout
 *
 * Each rail row focuses the map. A list beside a graph that cannot drive the graph is a second
 * thing to read, and a reader with two views of the same data and no link between them ends up
 * trusting neither.
 */

/**
 * A glyph per channel.
 *
 * Deliberately abstract rather than brand marks: these are drawn in our accent at 13px against a
 * dark rail, where a real logo would be both illegible and a trademark we do not have licence to
 * restyle. Their job is to make the row scannable by shape, and the label carries the name.
 */
function ChannelGlyph({ code }: { code: string }) {
  const common = {
    viewBox: '0 0 16 16', className: 'size-[13px] shrink-0', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.3, strokeLinecap: 'round' as const,
    'aria-hidden': true, focusable: 'false' as const,
  }
  switch (code) {
    case 'X':
      return <svg {...common}><path d="M3 3l10 10M13 3L3 13" /></svg>
    case 'FACEBOOK':
      return <svg {...common}><path d="M10 3H8.6A2.6 2.6 0 006 5.6V13M4 8h5" /></svg>
    case 'INSTAGRAM':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="10" height="10" rx="3" />
          <circle cx="8" cy="8" r="2.4" />
        </svg>
      )
    case 'TIKTOK':
      return (
        <svg {...common}>
          <path d="M9 3v7a2.6 2.6 0 11-2.6-2.6" />
          <path d="M9 5.2A3.4 3.4 0 0012.4 7" />
        </svg>
      )
    case 'YOUTUBE':
      return (
        <svg {...common}>
          <rect x="2.5" y="4" width="11" height="8" rx="2.4" />
          <path d="M7 6.6l3 1.9-3 1.9z" />
        </svg>
      )
    case 'TELEGRAM':
      return <svg {...common}><path d="M13.5 3.5L2.8 7.9l3.4 1.2 1 3.4 1.9-2.3 3.1 2.3z" /></svg>
    case 'SNAPCHAT':
      return <svg {...common}><path d="M8 3c2 0 3 1.4 3 3.3 0 2 .6 2.6 2 3.2-1 .8-1.4.6-1.6 1.4-.3 1-1.6.6-3.4.6s-3.1.4-3.4-.6c-.2-.8-.6-.6-1.6-1.4 1.4-.6 2-1.2 2-3.2C5 4.4 6 3 8 3z" /></svg>
    case 'REDDIT':
      return (
        <svg {...common}>
          <circle cx="8" cy="9" r="4.6" />
          <path d="M10.4 4.6L11 2.6l2 .5" />
        </svg>
      )
    case 'LINKEDIN':
      return <svg {...common}><path d="M4 6.6V12M4 4v.1M7.4 12V6.6M7.4 9a2.2 2.2 0 014.4 0v3" /></svg>
    case 'MANUAL':
      return <svg {...common}><path d="M3 12.4l1-3 6-6 2 2-6 6zM10 3.4l2 2" /></svg>
    default:
      return <svg {...common}><circle cx="8" cy="8" r="4.6" /></svg>
  }
}

/**
 * A rail section: collapsible, with its list capped and scrolling inside itself.
 *
 * ## Why the cap, and why it is on the LIST rather than the section
 *
 * These rails hold unbounded lists — a room can watch forty subjects and hundreds of narratives.
 * Left to grow, one long section pushes every other section off the bottom of the rail, and the
 * only way to reach the signal log is to scroll past four hundred narratives. Capping each list
 * at a fixed height and letting it scroll inside itself keeps every section reachable at all
 * times, which is the property that matters when somebody is looking for one specific thing
 * under pressure.
 *
 * The header stays outside the scroll area on purpose, so the count and the collapse control do
 * not scroll away from the list they describe.
 *
 * ## Collapsed state is remembered per section
 *
 * An operator who closes Channels because they only watch one platform should not have to close
 * it again on every visit. It is stored per browser, not per account — this is a preference
 * about a screen, not a fact about the room, and syncing it would be a promise we would then
 * have to keep.
 */
export function Block({ title, note, children, className, maxBodyHeight = 220, storageKey }: {
  title: string
  note?: string
  children: React.ReactNode
  className?: string
  /** Cap for the scrolling body, in px. Pass 0 to let the section grow freely. */
  maxBodyHeight?: number
  /** Stable key for remembering the collapsed state. Omit to make the section always open. */
  storageKey?: string
}) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return true
    try {
      return window.localStorage.getItem(`axis.console.rail.${storageKey}`) !== 'closed'
    } catch {
      // Private windows and blocked site data throw on access. A section that cannot remember
      // its state should still render — open, which is the useful default.
      return true
    }
  })

  const toggle = () => {
    setOpen((was) => {
      const next = !was
      if (storageKey) {
        try {
          window.localStorage.setItem(
            `axis.console.rail.${storageKey}`, next ? 'open' : 'closed',
          )
        } catch { /* preference only — never worth failing the interaction over */ }
      }
      return next
    })
  }

  const headingId = `rail-${storageKey ?? title.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section className={cn('border-b px-4 pt-3.5 pb-4', className)}
             style={{ borderColor: 'var(--graph-panel-line)' }}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        {storageKey ? (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={headingId}
            className="group flex items-center gap-1.5 text-left"
          >
            <span aria-hidden
                  className={cn('inline-block font-mono text-[8px] transition-transform',
                    open ? 'rotate-90' : 'rotate-0')}
                  style={{ color: 'var(--graph-ink-faint)' }}>▶</span>
            <h2 className="axis-console-eyebrow">{title}</h2>
          </button>
        ) : (
          <h2 className="axis-console-eyebrow">{title}</h2>
        )}
        {note ? (
          <em className="not-italic font-mono text-[9px] tracking-wider"
              style={{ color: 'var(--graph-ink-faint)' }}>{note}</em>
        ) : null}
      </div>
      <div
        id={headingId}
        hidden={!open}
        className={cn(maxBodyHeight > 0 && 'axis-console-scroll overflow-y-auto')}
        style={maxBodyHeight > 0 ? { maxHeight: maxBodyHeight } : undefined}
      >
        {children}
      </div>
    </section>
  )
}

/**
 * The platform filter.
 *
 * ## Every configured platform is listed, including the empty ones
 *
 * A ring that hides its quiet channels cannot show a channel going quiet — and a watch that
 * silently stopped returning results looks exactly like calm. A zero here is information.
 *
 * ## Watch-only is stated, not implied
 *
 * Half the platforms in this product have no write API. An operator reading a spike on TikTok
 * needs to know we cannot reply there *before* they plan a response, not after.
 */
export function PlatformFilter({ platforms, hidden, onToggle, onSolo, onHover }: {
  platforms: PlatformNode[]
  hidden: Set<string>
  onToggle: (code: string) => void
  onSolo: (code: string) => void
  onHover: (code: string | null) => void
}) {
  const peak = Math.max(1, ...platforms.map((p) => p.threads))
  return (
    <div className="space-y-0.5">
      {platforms.map((p) => {
        const off = hidden.has(p.code)
        return (
          <button
            key={p.code}
            type="button"
            onClick={() => onToggle(p.code)}
            onDoubleClick={() => onSolo(p.code)}
            onMouseEnter={() => onHover(p.code)}
            onMouseLeave={() => onHover(null)}
            title={`${p.label} — ${p.threads} threads${p.canPost ? '' : ' · watch only, no reply API'}\nClick to filter, double-click to isolate`}
            className={cn(
              'grid w-full grid-cols-[15px_1fr_auto] items-center gap-2 rounded-sm px-1.5 py-1',
              'text-left transition-colors hover:bg-white/[0.04]',
              off && 'opacity-35',
            )}
          >
            <span className="flex" style={{ color: 'var(--graph-channel)', opacity: 0.8 }}>
              <ChannelGlyph code={p.code} />
            </span>
            <span className="min-w-0 space-y-1">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-[11.5px]"
                      style={{ color: 'var(--graph-ink)' }}>{p.label}</span>
                {!p.canPost ? (
                  <span className="shrink-0 font-mono text-[7.5px] tracking-[0.16em]"
                        style={{ color: 'var(--graph-ink-faint)' }}>WATCH</span>
                ) : null}
              </span>
              <span className="block h-[2px] overflow-hidden bg-white/[0.06]">
                <span className="block h-full transition-[width] duration-500"
                      style={{
                        width: `${(p.threads / peak) * 100}%`,
                        background: 'var(--graph-channel)',
                        opacity: 0.85,
                      }} />
              </span>
            </span>
            <span className="text-right font-mono text-[10.5px] tabular-nums"
                  style={{ color: 'var(--graph-ink-dim)' }}>
              {p.threads}
              {p.aboveThreshold > 0 ? (
                <span className="ml-1" style={{ color: 'var(--severity-high)' }}>
                  ↑{p.aboveThreshold}
                </span>
              ) : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** Subjects, ranked by what is moving. Clicking one flies the map to it. */
export function SubjectList({ subjects, selectedId, onPick, threshold }: {
  subjects: SubjectNode[]
  selectedId: string | null
  onPick: (id: string) => void
  threshold: number
}) {
  if (!subjects.length) {
    return <Empty>No subject has a thread in this window.</Empty>
  }
  return (
    <div className="space-y-0.5">
      {subjects.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onPick(s.id)}
          className={cn(
            'flex w-full items-center gap-2 rounded-sm px-1.5 py-1.5 text-left',
            'transition-colors hover:bg-white/[0.04]',
            selectedId === s.id && 'bg-white/[0.06]',
          )}
        >
          <span className="size-1.5 shrink-0 rounded-full"
                style={{ background: threatInk(s.threatMax, threshold) }} />
          <span className="flex-1 truncate text-[11.5px]" style={{ color: 'var(--graph-ink)' }}>
            {s.name ?? 'Unnamed subject'}
          </span>
          <span className="font-mono text-[10px] tabular-nums"
                style={{ color: 'var(--graph-ink-faint)' }}>{s.threads}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * Narratives, ranked.
 *
 * Coordinated ones carry their own hue AND the word — the distinction between "forty people
 * said this" and "the same sentence appeared forty times" is the most consequential thing on
 * this screen, and it must not rest on a colour.
 */
export function NarrativeList({ narratives, selectedId, onPick, threshold }: {
  narratives: NarrativeNode[]
  selectedId: string | null
  onPick: (id: string) => void
  threshold: number
}) {
  if (!narratives.length) {
    return <Empty>Nothing has clustered in this window.</Empty>
  }
  return (
    <div className="space-y-0.5">
      {narratives.map((n) => {
        const coordinated = n.kind === 'COORDINATED'
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => onPick(n.id)}
            className={cn(
              'w-full rounded-sm px-1.5 py-1.5 text-left transition-colors hover:bg-white/[0.04]',
              selectedId === n.id && 'bg-white/[0.06]',
            )}
          >
            <span className="flex items-center gap-2">
              <span className={cn('size-1.5 shrink-0', coordinated ? 'rotate-45' : 'rounded-full')}
                    style={{
                      background: coordinated
                        ? 'var(--graph-coordinated)'
                        : threatInk(n.threatMax, threshold),
                    }} />
              <span className="flex-1 truncate text-[11.5px]"
                    style={{ color: 'var(--graph-ink)' }}>{n.label}</span>
              <span className="font-mono text-[10px] tabular-nums"
                    style={{ color: 'var(--graph-ink-faint)' }}>{n.threads}</span>
            </span>
            <span className="mt-0.5 block pl-[14px] font-mono text-[8.5px] tracking-[0.12em]"
                  style={{
                    color: coordinated ? 'var(--graph-coordinated)' : 'var(--graph-ink-faint)',
                  }}>
              {coordinated
                ? `COORDINATED · ${n.distinctAuthors} AUTHORS · ${n.coordinationConfidence}% CONF`
                : `ORGANIC · ${(n.subjectName ?? 'UNSCOPED').toUpperCase()}`}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * The council's alerts.
 *
 * ## Derived on every read, never stored
 *
 * An alert table drifts — the row still says "unanswered" long after somebody answered it, and
 * a board that cries wolf is a board people learn to scroll past. These recompute from the
 * assessments on every poll, so one disappears the moment its condition stops being true.
 *
 * ## Each one carries the council's own sentence
 *
 * An alert that says only "high threat" asks the reader to trust a number. One that carries
 * the rationale asks them to check it, which is the behaviour this room wants.
 */
export function AlertFeed({ alerts, onPick }: {
  alerts: GraphAlert[]
  onPick: (alert: GraphAlert) => void
}) {
  if (!alerts.length) {
    return <Empty>Nothing above the threshold, and nothing fast that has not been read.</Empty>
  }
  return (
    <div className="flex flex-col">
      {alerts.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onPick(a)}
          className="axis-console-in border-b border-white/[0.035] py-2 text-left
                     transition-colors last:border-b-0 hover:bg-white/[0.04]"
        >
          <span className="flex items-center gap-2">
            <span className="shrink-0 border px-1 py-px font-mono text-[7.5px] tracking-[0.16em]"
                  style={{ color: SEVERITY_INK[a.severity], borderColor: 'currentColor' }}>
              {a.severity}
            </span>
            <span className="flex-1 truncate text-[11px]" style={{ color: 'var(--graph-ink)' }}>
              {a.title}
            </span>
            {a.threat !== null ? (
              <span className="font-mono text-[10px] tabular-nums"
                    style={{ color: SEVERITY_INK[a.severity] }}>{a.threat}</span>
            ) : null}
          </span>
          <span className="mt-1 block line-clamp-2 text-[10.5px] leading-snug"
                style={{ color: 'var(--graph-ink-dim)' }}>
            {a.detail}
          </span>
          <span className="mt-1 flex items-center gap-2 font-mono text-[8.5px] tracking-[0.1em]"
                style={{ color: 'var(--graph-ink-faint)' }}>
            <span>{a.subjectName ?? 'UNSCOPED'}</span>
            <span>·</span>
            <span>{a.source === 'COUNCIL' ? 'AI COUNCIL' : 'ROOM'}</span>
            <span>·</span>
            <span>{fmtRelative(a.raisedAt)}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

// ── the right rail: what is selected ─────────────────────────────────────────

export function Cell({ label, value, ink }: {
  label: string
  value: React.ReactNode
  ink?: string
}) {
  return (
    <div className="px-2.5 py-2" style={{ background: 'var(--graph-panel)' }}>
      <i className="mb-1 block font-mono text-[8.5px] not-italic tracking-[0.18em] uppercase"
         style={{ color: 'var(--graph-ink-faint)' }}>{label}</i>
      <b className="font-mono text-[15px] font-normal tabular-nums"
         style={{ color: ink ?? 'var(--graph-ink)' }}>{value}</b>
    </div>
  )
}

export function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-px"
         style={{ background: 'var(--graph-panel-line)' }}>
      {children}
    </div>
  )
}

export function Pill({ children, ink }: { children: React.ReactNode, ink?: string }) {
  return (
    <span className="border px-2 py-0.5 font-mono text-[8.5px] tracking-[0.18em] uppercase"
          style={{ color: ink ?? 'var(--graph-ink-faint)', borderColor: 'currentColor' }}>
      {children}
    </span>
  )
}

export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 py-3 text-[11px] leading-relaxed"
       style={{ color: 'var(--graph-ink-faint)' }}>{children}</p>
  )
}

/** The board's own numbers, for the top bar. */
export function Stat({ label, value, hot, sub }: {
  label: string
  value: React.ReactNode
  hot?: boolean
  /**
   * The line that makes the number mean something to somebody who did not build this.
   *
   * "Above threshold: 12" is a reading an analyst can act on and a candidate cannot — the
   * threshold is a server setting they have never seen. With the scale said out loud ("7 or
   * more out of 10") the same tile answers the question instead of raising it.
   */
  sub?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <i className="font-mono text-[8.5px] not-italic tracking-[0.2em] uppercase"
         style={{ color: 'var(--graph-ink-faint)' }}>{label}</i>
      <b className="font-mono text-[15px] font-normal tabular-nums"
         style={{ color: hot ? 'var(--severity-high)' : 'var(--graph-ink)' }}>{value}</b>
      {sub ? (
        <i className="font-mono text-[8px] not-italic leading-none"
           style={{ color: 'var(--graph-ink-faint)' }}>{sub}</i>
      ) : null}
    </div>
  )
}

/**
 * A count of threads nothing has read.
 *
 * Rendered as its own statistic rather than folded into a total, and never as a zero-threat
 * number. The whole failure this board exists to prevent is a hostile thread passing as
 * harmless because nothing ever looked at it.
 */
export function UnreadStat({ unscored, of }: { unscored: number, of: number }) {
  return (
    <Stat
      label="Not read"
      value={`${fmtNumber(unscored)}/${fmtNumber(of)}`}
      hot={unscored > 0 && of > 0 && unscored / of > 0.25}
    />
  )
}

export type Picked =
  | { kind: 'subject', node: SubjectNode }
  | { kind: 'narrative', node: NarrativeNode }
  | { kind: 'thread', node: ThreadNode }
